import { FC, useContext, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { CarPlanning, getCarPlanningStates, getCarPlanningTypes, Status, Types } from "../../http/carPlannings/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { User } from "../../http/users/types";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormGrid from "../ui/FormGrid";
import Input from "../ui/Input";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";

const SUBSIDIARY_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARY_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

export interface CarPlanningFormBody {
  year: number;
  nr: string;
  driverName: string;
  startDate: string;
  carArrivalDate?: string;
  subsidiary: SubsidiarySearch;
  managerName: string;
  costCenter: string;
  responsible: User;
  state?: Status;
  type?: Types;
  priceWithoutVat?: number;
}

type CarPlanningFormProps = {
  carPlanning?: CarPlanning | null;
  onSubmit: (form: CarPlanningFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  // onUpdate?: () => void;
};

const CarPlanningForm: FC<CarPlanningFormProps> = ({
  carPlanning,
  onSubmit,
  isDisabled,
  isLoading,
}: // onUpdate,
CarPlanningFormProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  // const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  // const [openNewCarDriver, setOpenNewCarDriver] = useState<boolean>(false);

  const subsidiaryApi = useSubsidiariesApi();
  // const carDriversApi = useCarDriversApi();

  const states = getCarPlanningStates(i18n.t);
  const types = getCarPlanningTypes(i18n.t);
  const subsidiaries = subsidiaryApi.useAdminSubsidiaries(
    { limit: 100, fields: SUBSIDIARY_FIELDS },
    aborter.current.signal,
  );

  const defaultValues = {
    year: carPlanning?.year,
    nr: carPlanning?.nr,
    driverName: carPlanning?.driverName,
    managerName: carPlanning?.managerName,
    costCenter: carPlanning?.costCenter,
    startDate: carPlanning?.startDate,
    carArrivalDate: carPlanning?.carArrivalDate,
    subsidiary: carPlanning?.subsidiary,
    state: states.find((x) => x.value == carPlanning?.status),
    type: types.find((x) => x.value == carPlanning?.type),
    priceWithoutVat: carPlanning?.priceWithoutVat,
  };

  const handleAssign = useRef<(() => Promise<boolean> | undefined) | undefined>(undefined);

  // const onSubmitCarDriver = async (formData: CarDriverFormBody, ass?: CarCurrentAssignment) => {
  //   const start = convertLocalDateToUTC(formData.startDate);
  //   const end = formData.endDate ? convertLocalDateToUTC(formData.endDate) : undefined;
  //   const prev = ass && formData.prevEndDate ? convertLocalDateToUTC(formData.prevEndDate) : undefined;

  //   try {
  //     if (ass && prev) {
  //       await carDriversApi.assignNewDriver({
  //         carId: formData.car.id,
  //         driverId: formData.driver.id,
  //         startDate: start,
  //         endDate: end,
  //         typeOfUse: formData.typeOfUse.value,
  //         prevEndDate: prev,
  //         currentVersionNumber: ass.carDriver?.versionNumber ?? ass.otherCarAssignment?.versionNumber,
  //         carPlanningId: carPlanning?.id,
  //       });
  //     } else {
  //       await carDriversApi.insertAdminCarDriver({
  //         carId: formData.car.id,
  //         driverId: formData.driver.id,
  //         startDate: start,
  //         endDate: end,
  //         typeOfUse: formData.typeOfUse.value,
  //         carPlanningId: carPlanning?.id,
  //       });
  //     }
  //     dispatch(showSuccessNotification(i18n.t));
  //     onUpdate?.();
  //     setOpenNewCarDriver(false);
  //   } catch (error) {
  //     dispatch(showErrorNotification(i18n, error));
  //   }
  // };

  return (
    <>
      <Form<CarPlanningFormBody>
        defaultValues={defaultValues}
        submitText={i18n.t.common.save}
        disabled={isDisabled}
        onSubmit={() => onSubmit}
        // secondaryButton={
        //   carPlanning?.status !== "ordered" && carPlanning?.id && carPlanning.type !== "re-assignment" ? (
        //     <Button
        //       buttonProps={{
        //         onClick: async () => {
        //           const ok = await handleAssign.current?.();
        //           if (!ok) {
        //             return;
        //           }
        //           navigate(`/admin/car_purchases/new?car_planning_id=${carPlanning?.id}`);
        //         },
        //         disabled: isDisabled || isLoading,
        //       }}
        //     >
        //       {i18n.t.common.orderCar}
        //     </Button>
        //   ) : carPlanning?.status !== "ordered" &&
        //     carPlanning?.status !== "assigned" &&
        //     carPlanning?.id &&
        //     carPlanning.type === "re-assignment" ? (
        //     <Button
        //       buttonProps={{
        //         onClick: async () => {
        //           try {
        //             const ok = await handleAssign.current?.();
        //             if (!ok) {
        //               return;
        //             }
        //             setOpenNewCarDriver(true);
        //           } catch (error) {
        //             dispatch(showErrorNotification(i18n, error));
        //           }
        //         },
        //       }}
        //     >
        //       {i18n.t.common.assignCar}
        //     </Button>
        //   ) : undefined
        // }
        itemCreationInfo={carPlanning}
      >
        {({ register, formState: { errors }, control, reset, watch, setValue, trigger, getValues }) => {
          useEffect(() => {
            reset(defaultValues);

            handleAssign.current = async () => {
              const valid = await trigger();
              if (!valid) {
                return false;
              }

              const data = getValues();
              try {
                await onSubmit(data);
                return true;
              } catch (error) {
                dispatch(showErrorNotification(i18n, error));
                return false;
              }
            };
          }, [carPlanning]);

          return (
            <FormGrid columns={2}>
              <Controller
                name="year"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.year} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        disabled={isDisabled}
                        isInvalid={isInvalid}
                        onBlur={(value) => value && setValue("year", value)}
                        decimalScale={0}
                        allowNegative={false}
                        maxLength={4}
                      />
                    )}
                  </FormField>
                )}
              />

              <FormField label={i18n.t.common.number} error={errors.nr}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={
                      isDisabled || isLoading || carPlanning?.status == "assigned" || carPlanning?.status == "ordered"
                    }
                    {...register("nr", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              <FormField label={i18n.t.common.driverName} error={errors.driverName}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={
                      isDisabled || isLoading || carPlanning?.status == "assigned" || carPlanning?.status == "ordered"
                    }
                    {...register("driverName", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              <FormField label={i18n.t.common.managerName} error={errors.managerName}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={
                      isDisabled || isLoading || carPlanning?.status == "assigned" || carPlanning?.status == "ordered"
                    }
                    {...register("managerName", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              <FormField label={i18n.t.common.costCenter} error={errors.costCenter}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={
                      isDisabled || isLoading || carPlanning?.status == "assigned" || carPlanning?.status == "ordered"
                    }
                    {...register("costCenter", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              <Controller
                name="startDate"
                control={control}
                rules={{
                  validate: (value) => {
                    const d = new Date(value!);
                    if (d.getFullYear() < 1000) {
                      return i18n.t.common.invalidDate;
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.startDate} error={fieldState.error} isOptional>
                    {({ isInvalid }) => (
                      <DatePicker
                        value={watch("startDate")}
                        onChange={(v) => setValue("startDate", v!)}
                        disabled={isDisabled || isLoading}
                        isInvalid={isInvalid}
                        clearable
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="carArrivalDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!!value) {
                      const d = new Date(value);
                      if (d.getFullYear() < 1000) {
                        return i18n.t.common.invalidDate;
                      }
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.plannedArrivalDate} error={fieldState.error} isOptional>
                    {({ isInvalid }) => (
                      <DatePicker
                        value={watch("carArrivalDate")}
                        onChange={(v) => setValue("carArrivalDate", v!)}
                        disabled={isDisabled || isLoading}
                        isInvalid={isInvalid}
                        clearable
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="subsidiary"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={subsidiaries.data?.items}
                        isLoading={isLoading}
                        isDisabled={isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="state"
                control={control}
                // disabled={!showAssign}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.state} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={states}
                        isLoading={isLoading}
                        isDisabled={
                          isDisabled ||
                          isLoading ||
                          carPlanning?.status == "assigned" ||
                          carPlanning?.status == "ordered"
                        }
                        isClearable
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<Status>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="type"
                control={control}
                // disabled={!showAssign}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.type} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={types}
                        isLoading={isLoading}
                        isDisabled={
                          isDisabled ||
                          isLoading ||
                          carPlanning?.status == "assigned" ||
                          carPlanning?.status == "ordered"
                        }
                        isClearable
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<Types>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="priceWithoutVat"
                control={control}
                // rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.priceWithoutVat} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        disabled={isDisabled}
                        isInvalid={isInvalid}
                        onBlur={(value) => value && setValue("priceWithoutVat", value)}
                        decimalScale={2}
                        allowNegative={false}
                        suffix=" €"
                      />
                    )}
                  </FormField>
                )}
              />
            </FormGrid>
          );
        }}
      </Form>

      {/* {openNewCarDriver && (
        <ModalDialog onClose={() => setOpenNewCarDriver(false)} title={i18n.t.common.assignCar}>
          <CarDriverForm onSubmit={onSubmitCarDriver} />
        </ModalDialog>
      )} */}
    </>
  );
};

export default CarPlanningForm;
